import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inr, fmtDate, errMsg } from "../../lib/format";
import { StatusBadge } from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Plus, CheckCheck, X } from "lucide-react";

export default function AdminWithdrawals() {
  const [txns, setTxns] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [utrEdit, setUtrEdit] = useState({});
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ user_id: "", amount: "", upi_id: "", bank_details: "" });

  const load = () => api.get("/admin/transactions?type=withdrawal").then((r) => { setTxns(r.data); setSelected([]); });
  useEffect(() => { load(); api.get("/admin/users").then((r) => setUsers(r.data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/withdrawals", { ...form, amount: Number(form.amount) });
      toast.success("Withdrawal created");
      setOpen(false);
      setForm({ user_id: "", amount: "", upi_id: "", bank_details: "" });
      load();
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  const setStatus = async (t, status) => {
    if (status === "paid" && !(utrEdit[t.id] || t.utr)) {
      return toast.error("Enter UTR before marking as Paid");
    }
    try {
      await api.patch(`/admin/transactions/${t.id}`, { status, utr: utrEdit[t.id] || t.utr || "" });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  const canAct = (s) => s === "pending" || s === "processing";
  const actionable = txns.filter((t) => canAct(t.status));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === actionable.length ? [] : actionable.map((t) => t.id)));

  const bulk = async (status) => {
    try {
      const { data } = await api.post("/admin/transactions/bulk", { ids: selected, status });
      toast.success(`${data.updated} withdrawal(s) marked ${status}`);
      load();
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><p className="overline">Outgoing</p><h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Withdrawals</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-withdrawal-btn" className="rounded-sm h-10 gap-2"><Plus size={16} /> Manual Payout</Button></DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="font-heading">Create Manual Withdrawal</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="overline">User</Label>
                <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                  <SelectTrigger data-testid="withdrawal-user-select" className="mt-2 rounded-sm h-11"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {inr(u.balance)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="overline">Amount (₹)</Label><Input data-testid="withdrawal-amount-input" type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
              <div><Label className="overline">UPI ID</Label><Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
              <div><Label className="overline">Bank Details</Label><Textarea value={form.bank_details} onChange={(e) => setForm({ ...form, bank_details: e.target.value })} rows={2} className="mt-2 rounded-sm" /></div>
              <DialogFooter><Button data-testid="withdrawal-create-submit" type="submit" disabled={!form.user_id} className="rounded-sm w-full h-11">Create Withdrawal</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {selected.length > 0 && (
        <div data-testid="withdrawal-bulk-bar" className="flex flex-wrap items-center gap-3 mb-4 px-4 py-3 bg-[#059669]/10 border border-[#059669]/30 rounded-sm">
          <span className="text-sm font-semibold">{selected.length} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button data-testid="bulk-pay-withdrawals" onClick={() => bulk("paid")} className="h-9 rounded-sm bg-[#059669] hover:bg-[#047857] gap-1.5"><CheckCheck size={15} /> Mark Paid</Button>
            <Button data-testid="bulk-reject-withdrawals" onClick={() => bulk("rejected")} variant="outline" className="h-9 rounded-sm gap-1.5"><X size={15} /> Reject Selected</Button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-x-auto bg-card">
        <table className="w-full text-sm" data-testid="admin-withdrawals-table">
          <thead><tr className="text-left overline border-b border-border">
            <th className="px-5 py-3 w-10">
              {actionable.length > 0 && <Checkbox data-testid="withdrawal-select-all" checked={selected.length === actionable.length && actionable.length > 0} onCheckedChange={toggleAll} />}
            </th>
            <th className="px-5 py-3 font-semibold">User</th><th className="px-5 py-3 font-semibold">Amount</th>
            <th className="px-5 py-3 font-semibold">Payout To</th><th className="px-5 py-3 font-semibold">UTR</th>
            <th className="px-5 py-3 font-semibold hidden lg:table-cell">Date</th><th className="px-5 py-3 font-semibold">Status</th><th className="px-5 py-3 font-semibold">Action</th>
          </tr></thead>
          <tbody>
            {txns.length === 0 ? (<tr><td colSpan="8" className="px-5 py-6 text-muted-foreground">No withdrawals.</td></tr>) : txns.map((t) => (
              <tr key={t.id} className="border-b border-border/40 last:border-0 align-top">
                <td className="px-5 py-3">
                  {canAct(t.status) && <Checkbox data-testid={`withdrawal-select-${t.id}`} checked={selected.includes(t.id)} onCheckedChange={() => toggle(t.id)} />}
                </td>
                <td className="px-5 py-3">{t.user_name || t.user_email}</td>
                <td className="px-5 py-3 font-mono-num font-medium">{inr(t.amount)}</td>
                <td className="px-5 py-3 text-xs max-w-[160px]">{t.upi_id || t.bank_details || "—"}</td>
                <td className="px-5 py-3">
                  {canAct(t.status) ? (
                    <Input data-testid={`withdrawal-utr-${t.id}`} defaultValue={t.utr} onChange={(e) => setUtrEdit({ ...utrEdit, [t.id]: e.target.value })} placeholder="UTR" className="h-8 w-32 rounded-sm font-mono-num text-xs" />
                  ) : <span className="font-mono-num text-xs">{t.utr || "—"}</span>}
                </td>
                <td className="px-5 py-3 text-xs text-muted-foreground hidden lg:table-cell">{fmtDate(t.created_at)}</td>
                <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-5 py-3">
                  {canAct(t.status) ? (
                    <div className="flex flex-wrap gap-2">
                      {t.status === "pending" && <Button data-testid={`process-withdrawal-${t.id}`} onClick={() => setStatus(t, "processing")} variant="outline" className="h-8 rounded-sm text-xs">Process</Button>}
                      <Button data-testid={`pay-withdrawal-${t.id}`} onClick={() => setStatus(t, "paid")} className="h-8 rounded-sm bg-[#059669] hover:bg-[#047857] text-xs">Mark Paid</Button>
                      <Button data-testid={`reject-withdrawal-${t.id}`} onClick={() => setStatus(t, "rejected")} variant="outline" className="h-8 rounded-sm text-xs">Reject</Button>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
